import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar/Sidebar';
import SessionsChart from './components/SessionsChart/SessionsChart';
import DriversRank from './components/DriversRank/DriversRank';
import TrendAnalysis from './components/TrendAnalysis/TrendAnalysis';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import TrendAnalysisPage from './pages/TrendAnalysisPage/TrendAnalysisPage';
import UserWiseReportPage from './pages/UserWiseReportPage/UserWiseReportPage';
import SettingsPage from './pages/SettingsPage/SettingsPage';
import LoginPage from './pages/LoginPage/LoginPage';
import DriverScoreCategoryPage from './pages/DriverScoreCategoryPage/DriverScoreCategoryPage';
import DriverSegmentationPage from './pages/DriverSegmentationPage/DriverSegmentationPage';
import SessionwisePieChart from './components/SessionwisePieChart/SessionwisePieChart';
import ViolationDescriptionManager from './components/ViolationDescriptionManager/ViolationDescriptionManager';
import { LOGOS, ICONS } from './config/logoConfig';
import { API_BASE_URL } from './api/apiClient';

function App() {
  const navigate = useNavigate();
  const [selectedVehicle, setSelectedVehicle] = useState(''); // Start with empty string
  const [userName, setUserName] = useState('Admin User');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebarOpen');
      return stored === null ? true : stored === 'true';
    } catch (error) {
      return true;
    }
  });
  const dropdownRef = useRef(null);
  const initialVehicleLoaded = useRef(false);

  // Fetch vehicle types and select the first one by default
  const fetchAndSelectDefaultVehicle = async (locationId) => {
    // Only fetch and set default vehicle if it hasn't been done already
    if (initialVehicleLoaded.current) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/vehicle-types?locationId=${locationId}`);
      if (!response.ok) throw new Error('Failed to fetch vehicle types');
      const data = await response.json();
      
      if (data && data.length > 0) {
        console.log('Setting default vehicle:', data[0].VehicleID);
        setSelectedVehicle(data[0].VehicleID.toString());
        initialVehicleLoaded.current = true;
      }
    } catch (error) {
      console.error('Error fetching default vehicle:', error);
    }
  };

  // Check if user is already logged in on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setUserName(parsedUser.name);
        setIsAuthenticated(true);
        
        // Fetch and select default vehicle using user's location
        if (parsedUser.locationId) {
          fetchAndSelectDefaultVehicle(parsedUser.locationId);
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Function to handle login
  const handleLogin = (userData) => {
    setUser(userData);
    setUserName(userData.name);
    setIsAuthenticated(true);
    
    // Fetch and select default vehicle using user's location
    if (userData.locationId) {
      fetchAndSelectDefaultVehicle(userData.locationId);
    }
    
    navigate('/dashboard');
  };

  const handleLogout = () => {
    // Clear auth state and localStorage
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('user');
    navigate('/login');
    setIsDropdownOpen(false);
  };

  const handleVehicleSelect = (vehicleId) => {
    console.log(`Selected vehicle ID: ${vehicleId}`);
    // Ensure vehicleId is a string
    setSelectedVehicle(vehicleId ? vehicleId.toString() : '');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebarOpen', String(next));
      } catch (error) {
        console.error('Error saving sidebar state:', error);
      }
      return next;
    });
  };

  useEffect(() => {
    // Handle clicks outside the dropdown to close it
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const menuItems = [
    { 
      icon: '⬜', 
      label: 'DASHBOARD',
      onClick: () => navigate('/dashboard')
    },
    { 
      icon: '⚙️', 
      label: 'SETTINGS',
      onClick: () => navigate('/settings')
    },
    { 
      icon: '📄', 
      label: 'REPORT',
      onClick: () => navigate('/reports')
    },
    { 
      icon: '📝', 
      label: 'VIOLATION DESCRIPTIONS',
      onClick: () => navigate('/violation-descriptions')
    }
  ];

  // Protected route component
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    return children;
  };

  // Main app layout with header and sidebar
  const MainLayout = ({ children }) => {
    // Function to render user avatar - either photo from DB or default avatar
    const renderUserAvatar = () => {
      if (user && user.photo) {
        // If user has a photo, display it
        return (
          <div 
            className="user-avatar" 
            style={{ 
              backgroundImage: `url(data:image/jpeg;base64,${user.photo})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></div>
        );
      } else {
        // Default avatar with user initials if no photo
        const initials = userName ? userName.split(' ').map(name => name[0]).join('').toUpperCase() : 'U';
        return (
          <div className="user-avatar default-avatar">
            {initials}
          </div>
        );
      }
    };

    return (
      <div className="app">
        <div className="main-header">
          <div className="header-left">
            <button
              type="button"
              className={`sidebar-toggle-btn ${isSidebarOpen ? 'is-open' : 'is-closed'}`}
              onClick={toggleSidebar}
              aria-label={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              aria-expanded={isSidebarOpen}
            >
              <span className="sidebar-toggle-icon" aria-hidden="true">☰</span>
            </button>
            <div className="header-logo">
              <img src={LOGOS.DRIVE360.DEFAULT} alt="Drive360 Logo" />
            </div>
          </div>
          <div className="header-actions">
            <div className="user-profile-container" ref={dropdownRef}>
              <div className="user-profile" onClick={toggleDropdown}>
                {renderUserAvatar()}
                <div className="user-info">
                  <div className="user-greeting">Hello,</div>
                  <div className="user-names">{userName}</div>
                  {user && <div className="user-role">{user.roleDescription}</div>}
                </div>
                <span className="dropdown-icon">{isDropdownOpen ? '▲' : '▼'}</span>
              </div>
              {isDropdownOpen && (
                <div className="user-dropdown-menu">
                  <div className="dropdown-item" onClick={() => {
                    navigate('/settings');
                    setIsDropdownOpen(false);
                  }}>
                    <span className="settings-icon">⚙️</span>
                    Settings
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item logout-item" onClick={handleLogout}>
                    <img src={ICONS.NAVIGATION.LOGOUT} alt="Logout" className="logout-icon" />
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className={`content-container ${isSidebarOpen ? '' : 'sidebar-hidden'}`.trim()} style={{ position: 'relative' }}>
          <ErrorBoundary>
            <Sidebar 
              onLogout={handleLogout}
              onVehicleTypeSelect={handleVehicleSelect}
              selectedVehicleId={selectedVehicle}
              menuItems={menuItems}
              isOpen={isSidebarOpen}
              style={{ borderLeft: 'none', position: 'relative', zIndex: 10 }}
            />
          </ErrorBoundary>
          
          <div
            className="sidebar-blocker"
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: isSidebarOpen ? '280px' : '0', 
              height: '100%', 
              zIndex: 5,
              pointerEvents: 'none'
            }}
          ></div>
          
          {children}
        </div>
      </div>
    );
  };

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? 
        <Navigate to="/dashboard" /> : 
        <LoginPage onLogin={handleLogin} />
      } />
      
      <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <div className="dashboard-grid">
                  <ErrorBoundary>
                    <SessionsChart vehicleId={selectedVehicle} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <SessionwisePieChart vehicleId={selectedVehicle} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <TrendAnalysis vehicleId={selectedVehicle} />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <DriversRank vehicleId={selectedVehicle} />
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/trend-analysis" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <TrendAnalysisPage />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/user-wise-report" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <UserWiseReportPage />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <SettingsPage />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <h2>Reports Page</h2>
                <p>Reports content will go here</p>
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/driver-score-category" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <DriverScoreCategoryPage />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/driver-segmentation" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <DriverSegmentationPage />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/violation-descriptions" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="main-content">
              <div className="scrollable-content">
                <ViolationDescriptionManager />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default App;
